/**
 * EntityCollision — Single Responsibility: geometría de colisión para entidades.
 *
 * Todas las funciones son PURAS: no mutan estado, no importan Three.js/DOM.
 * Contrato público:
 *   collidesWith(a, b, margin?) → boolean
 *   collidesWithBox(entity, box, margin?) → boolean
 *   isTerrainPassable(entity, x, z, zones) → boolean
 *   getBoxContactNormal(entity, box) → { nx, nz }
 *   clampToBounds(x, z, bounds) → [number, number]
 */

/**
 * Colisión círculo-círculo entre dos entidades.
 * @param {{ x: number, z: number, radius: number, isDead: boolean }} a
 * @param {{ x: number, z: number, radius: number, isDead: boolean }} b
 * @param {number} [margin=0.25]
 * @returns {boolean}
 */
export function collidesWith(a, b, margin = 0.25) {
  if (!b || b.isDead || b === a) return false;
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const dist = Math.hypot(dx, dz);
  const combinedRadius = (a.radius + b.radius) / 16 + margin;
  return dist < combinedRadius;
}

/**
 * Colisión círculo-AABB (Axis-Aligned Bounding Box).
 * @param {{ x: number, z: number, radius: number, isDead?: boolean }} entity
 * @param {{ minX: number, maxX: number, minZ: number, maxZ: number }} box
 * @param {number} [margin=0]
 * @returns {boolean}
 */
export function collidesWithBox(entity, box, margin = 0) {
  if (entity.isDead) return false;
  const closestX = Math.max(box.minX, Math.min(box.maxX, entity.x));
  const closestZ = Math.max(box.minZ, Math.min(box.maxZ, entity.z));
  const dx = entity.x - closestX;
  const dz = entity.z - closestZ;
  const r = entity.radius / 16 + margin;
  return (dx * dx + dz * dz) < (r * r);
}

/**
 * Verifica si la posición (x, z) es navegable (no bloqueada por zonas impasables).
 * @param {{ radius: number }} entity
 * @param {number} x
 * @param {number} z
 * @param {Array<{ type: string, x?: number, z?: number, radius?: number, minX?: number, maxX?: number, minZ?: number, maxZ?: number }>} impassableZones
 * @returns {boolean}
 */
export function isTerrainPassable(entity, x, z, impassableZones = []) {
  const r = entity.radius / 16;
  return !impassableZones.some(zone => {
    if (zone.type === 'circle') {
      return Math.hypot(x - zone.x, z - zone.z) < (zone.radius + r);
    }
    if (zone.type === 'box') {
      const closestX = Math.max(zone.minX, Math.min(zone.maxX, x));
      const closestZ = Math.max(zone.minZ, Math.min(zone.maxZ, z));
      const dx = x - closestX;
      const dz = z - closestZ;
      return (dx * dx + dz * dz) < (r * r);
    }
    return false;
  });
}

/**
 * Calcula el vector normal de contacto entre una entidad y un AABB.
 * @param {{ x: number, z: number }} entity
 * @param {{ minX: number, maxX: number, minZ: number, maxZ: number }} box
 * @returns {{ nx: number, nz: number }}
 */
export function getBoxContactNormal(entity, box) {
  const closestX = Math.max(box.minX, Math.min(box.maxX, entity.x));
  const closestZ = Math.max(box.minZ, Math.min(box.maxZ, entity.z));
  const nx = entity.x - closestX;
  const nz = entity.z - closestZ;
  const len = Math.hypot(nx, nz);
  if (len > 0.0001) {
    return { nx: nx / len, nz: nz / len };
  }
  return { nx: 1, nz: 0 };
}

/**
 * Restringe (x, z) a los límites del mapa.
 * @param {number} x
 * @param {number} z
 * @param {{ minX: number, maxX: number, minZ: number, maxZ: number }} bounds
 * @returns {[number, number]}
 */
export function clampToBounds(x, z, bounds = { minX: -14, maxX: 14, minZ: -14, maxZ: 14 }) {
  return [
    Math.max(bounds.minX, Math.min(bounds.maxX, x)),
    Math.max(bounds.minZ, Math.min(bounds.maxZ, z))
  ];
}
